import React, { useState } from 'react';
import { Grade, Subject, WorksheetMode, DensityLevel, Tenant, UserProfile } from '../types';
import { Dropdown } from './Dropdown';
import { GRADES, SUBJECTS, DENSITY_LEVELS } from '../constants';
import { runService } from '../services/runService';
import { generateWorksheetPdf, WorksheetPdfInputs } from '../utils/worksheetPdfGenerator';
import { FileUpload } from './FileUpload';
import { extractTextFromPdf } from '../utils/pdfUtils';
import { useAuth } from '../contexts/AuthContext';


interface PracticeMakesPermanentModuleProps {
  tenant: Tenant;
  userProfile: UserProfile;
  brandLogo: string;
  partnerLogo: string;
}

export interface PromptInputSummary {
  grade: Grade;
  topic: string;
}

export const PracticeMakesPermanentModule: React.FC<PracticeMakesPermanentModuleProps> = ({ tenant, userProfile, brandLogo, partnerLogo }) => {
  const [showInputForm, setShowInputForm] = useState<boolean>(false);
  
  // Form State
  const [mode, setMode] = useState<WorksheetMode>(WorksheetMode.GENERIC);
  const [grade, setGrade] = useState<Grade>(Grade.GRADE_8);
  const [subject, setSubject] = useState<Subject>(Subject.SOCIAL_STUDIES);
  const [chapterTitle, setChapterTitle] = useState<string>('');
  const [chapterFile, setChapterFile] = useState<File | null>(null);
  const [densityLevel, setDensityLevel] = useState<DensityLevel>(DensityLevel.MEDIUM);
  const [diagnosticFile, setDiagnosticFile] = useState<File | null>(null);

  // App Logic State
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedWorksheet, setGeneratedWorksheet] = useState<string | null>(null);
  const [worksheetInputs, setWorksheetInputs] = useState<WorksheetPdfInputs | null>(null);
  const [tokenUsage, setTokenUsage] = useState<{ input: number; output: number; model: string; } | null>(null);
  const [isCopied, setIsCopied] = useState<boolean>(false);

  const { updateTenant } = useAuth();

  const handleGenerateWorksheet = async () => {
    if (!chapterTitle.trim() || !chapterFile) {
      setError("Please provide a chapter title and upload a chapter file.");
      return;
    }
    if (mode === WorksheetMode.DIAGNOSTIC_BASED && !diagnosticFile) {
      setError("Please upload the A-Typical diagnostic summary file for this mode.");
      return;
    }

    setError(null);
    setLoading(true);
    setGeneratedWorksheet(null);
    setTokenUsage(null);

    try {
      const result = await runService.startRun(tenant, userProfile, 'practice', {
        mode,
        grade,
        subject,
        chapterTitle,
        chapterFile,
        densityLevel: mode === WorksheetMode.GENERIC ? densityLevel : undefined,
        diagnosticFile: mode === WorksheetMode.DIAGNOSTIC_BASED ? diagnosticFile : undefined,
      });
      
      const { resultText, promptTokens, completionTokens, model } = result.aiResult;

      setWorksheetInputs({ grade, subject, chapterTitle });
      setGeneratedWorksheet(resultText);
      setTokenUsage({ input: promptTokens, output: completionTokens, model });
      
      updateTenant(result.newTenant);

      setShowInputForm(false);

    } catch (err: any) {
      console.error("Worksheet generation failed:", err);
      setError(err.message || "Failed to generate worksheet. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  
  const handleCopy = () => {
    if (generatedWorksheet) {
        navigator.clipboard.writeText(generatedWorksheet);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2500);
    }
  };

  const handleDownloadPdf = async () => {
  if (generatedWorksheet && worksheetInputs) {
    try {
      await generateWorksheetPdf(
        generatedWorksheet,
        worksheetInputs,
        brandLogo,
        partnerLogo,
        tenant?.tenantLogoUrl
      );
    } catch (e) {
      console.error("PDF generation failed:", e);
    }
  } else {
    console.error("Cannot generate PDF: Missing worksheet text or input summary");
  }
};


  const resetFormState = () => {
    setMode(WorksheetMode.GENERIC);
    setGrade(Grade.GRADE_8);
    setSubject(Subject.SOCIAL_STUDIES);
    setChapterTitle('');
    setChapterFile(null);
    setDensityLevel(DensityLevel.MEDIUM);
    setDiagnosticFile(null);
    setLoading(false);
    setError(null);
    setGeneratedWorksheet(null);
    setWorksheetInputs(null);
    setTokenUsage(null);
  };
  
  const renderInitialView = () => (
    <div
      onClick={() => { resetFormState(); setShowInputForm(true); }}
      className="flex-grow flex flex-col justify-center items-center text-center cursor-pointer hover:bg-gray-50 transition-colors duration-200 p-4 rounded-b-xl"
      role="button"
      aria-label="Open Practice Worksheet Generator Module"
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-brand-orange mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
      <p className="text-brand-grey text-lg mb-6 max-w-xs">
        Diagnostic-driven practice prompts that strengthen recall, application, and reasoning.
      </p>
      <p className="text-brand-orange font-semibold mt-auto pt-4">
        Generate a worksheet →
      </p>
    </div>
  );

  const renderInputForm = () => (
    <>
      <button onClick={() => { resetFormState(); setShowInputForm(false); }} className="mb-6 px-4 py-2 bg-gray-200 text-brand-grey text-sm font-semibold rounded-md shadow-sm hover:bg-gray-300">
        ← Back
      </button>

      {/* Mode Selector */}
      <div className="mb-6">
        <label className="block text-sm font-bold text-brand-grey mb-2">Generation Mode</label>
        <div className="flex space-x-4 rounded-lg bg-gray-100 p-1">
          <button onClick={() => setMode(WorksheetMode.GENERIC)} className={`w-full py-2 px-4 rounded-md text-sm font-semibold transition-colors ${mode === WorksheetMode.GENERIC ? 'bg-brand-orange text-white shadow' : 'text-gray-600 hover:bg-gray-200'}`}>
            Generic
          </button>
          <button onClick={() => setMode(WorksheetMode.DIAGNOSTIC_BASED)} className={`w-full py-2 px-4 rounded-md text-sm font-semibold transition-colors ${mode === WorksheetMode.DIAGNOSTIC_BASED ? 'bg-brand-orange text-white shadow' : 'text-gray-600 hover:bg-gray-200'}`}>
            Diagnostic-Based
          </button>
        </div>
      </div>
      
      {/* Common Inputs */}
      <div className="grid grid-cols-2 gap-4">
        <Dropdown label="Grade" options={GRADES} selectedValue={grade} onValueChange={(v) => setGrade(v as Grade)} id="practice-grade"/>
        <Dropdown label="Subject" options={SUBJECTS} selectedValue={subject} onValueChange={(v) => setSubject(v as Subject)} id="practice-subject"/>
      </div>
      <div className="mb-4">
        <label htmlFor="chapter-title" className="block text-sm font-bold text-brand-grey mb-1">Chapter Title</label>
        <input type="text" id="chapter-title" value={chapterTitle} onChange={(e) => setChapterTitle(e.target.value)} placeholder="e.g., The Indian Constitution" className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-brand-grey" required />
      </div>
      
      <FileUpload
        label="Chapter Content (PDF Required)"
        accept=".pdf"
        onFileChange={(files) => setChapterFile(files[0] || null)}
      />
      
      {/* Conditional Inputs */}
      {mode === WorksheetMode.GENERIC ? (
        <Dropdown label="Practice Density" options={DENSITY_LEVELS} selectedValue={densityLevel} onValueChange={(v) => setDensityLevel(v as DensityLevel)} id="practice-density"/>
      ) : (
        <FileUpload
          label="A-Typical Diagnostic Summary (PDF)"
          accept=".pdf"
          onFileChange={(files) => setDiagnosticFile(files[0] || null)}
        />
      )}

      {error && <p className="mb-4 text-sm text-red-600">{error}</p>}
      
      <button onClick={handleGenerateWorksheet} disabled={loading} className={`w-full px-6 py-3 bg-brand-orange text-white text-lg font-semibold rounded-md shadow-md hover:bg-opacity-90 ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}>
        {loading ? 'Generating Worksheet...' : 'Generate Worksheet'}
      </button>
    </>
  );

  const renderResultsView = () => (
    <>
      <div className="mb-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold text-brand-grey mb-2">Worksheet for: {worksheetInputs?.chapterTitle}</h3>
        <p className="text-sm text-gray-600">{worksheetInputs?.grade} | {worksheetInputs?.subject}</p>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 text-brand-grey">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-brand-grey">Generated Worksheet & Rubrics</h3>
          <div className="flex items-center space-x-2">
            <button onClick={handleDownloadPdf} className="px-3 py-2 bg-white border border-brand-orange text-brand-orange text-sm font-semibold rounded-md shadow-sm hover:bg-orange-50">
                Download PDF
            </button>
            <button onClick={handleCopy} className={`px-4 py-2 text-sm font-semibold rounded-md shadow-sm ${isCopied ? 'bg-green-600 text-white' : 'bg-brand-orange text-white hover:bg-opacity-90'}`}>
              {isCopied ? 'Copied!' : 'Copy Text'}
            </button>
          </div>
        </div>
        <pre className="whitespace-pre-wrap break-words text-sm bg-white p-4 rounded-md border border-gray-300 max-h-80 overflow-y-auto font-sans">
          {generatedWorksheet}
        </pre>
      

      </div>
      <button onClick={() => { resetFormState(); setShowInputForm(true); }} className="mt-6 w-full px-6 py-3 bg-brand-orange text-white text-lg font-semibold rounded-md shadow-md hover:bg-opacity-90">
        Generate New Worksheet
      </button>
    </>
  );

  return (
    <div className="bg-white shadow-xl rounded-xl border border-gray-200 p-8 md:p-10 lg:p-12 flex flex-col h-full transition-all duration-300 ease-in-out hover:shadow-2xl hover:-translate-y-2">
      <h2 className="text-3xl font-extrabold text-brand-grey mb-8 border-b-2 border-brand-orange pb-2">
        <span className="text-brand-orange">Practice</span> Makes <span className="text-brand-orange">Permanent</span><sup className="text-sm">™</sup>
      </h2>
      <div className="flex-grow flex flex-col">
        {generatedWorksheet && worksheetInputs
          ? renderResultsView()
          : showInputForm
          ? renderInputForm()
          : renderInitialView()}
      </div>
    </div>
  );
};